import useBotNameInput from "features/bots/BotForm/General/useBotNameInput";
import Title from "common/Typography/Title";
import React, { useContext, useEffect, useState } from "react";
import { BotFormContext } from "@shared/features/bots/BotForm/common/BotForm.context";
import AmountSelector from "features/bots/BotForm/General/AmountSelector";
import styled, { useTheme } from "styled-components";
import Flex from "common/Boxes/Flex";
import useSymbolAndAccountSelectors from "features/bots/BotForm/common/useSymbolAndAccountSelectors";
import Button from "common/Buttons/Button";
import Link from "common/Typography/Link";
import { useHistory } from "react-router-dom";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import SaveBotButton from "features/bots/common/SaveBotButton";
import { FormFieldTitle } from "features/bots/common/styles";
import { AnalyticsContext } from "analytics/AnalyticsProvider";
import { DcaBotCreateeditOrigin } from "analytics/events";
import useMeasure from "utils/hooks/useMeasure";
import EqualColumnsGrid from "common/Grids/EqualColumnsGrid";
import BotFormSummary from "features/bots/BotForm/common/BotFormSummary";
import useMarginForm from "features/bots/BotForm/General/useMarginForm";
import { getCoinExtraDetailsRoute, getPublicBotEditPath } from "utils/routes";
import { OpenInNewIcon } from "common/Icons/Icons";
import BotConfigurationModal from "features/bots/BotForm/common/BotConfigurationModal";
import { isSymbol } from "@shared/types/utils";
import GridsSelector from "features/bots/BotForm/Strategies/GridsSelector";
import { BotType } from "@shared/types";
import useQuery from "utils/hooks/useQuery";
import useAdvanced from "@shared/features/bots/BotForm/useAdvanced";
import { openMessage } from "utils/misc";
import SubGroupSelect from "features/bots/BotForm/General/SubGroupSelect";
import SignalsSummary from "features/bots/BotForm/Summaries/SignalsSummary";
import { paddingStyles } from "features/bots/BotForm/common/styles";
import { BOT_RANK_QUERY_PARAM } from "features/bots/common/constants";
import Tooltip from "common/Tooltips/Tooltip";
import useAlternativeUsdPairs from "@shared/features/bots/BotForm/General/useAlternativeUsdPairs.ts";
import MultiSymbolMarginSelect from "features/bots/BotForm/MultiSymbolMargin/MultiSymbolMarginSelect.tsx";
import { useBottomNavigator } from "utils/hooks/misc.tsx";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";
import useKeywordToSymbol from "@shared/features/bots/BotForm/General/useKeywordToSymbol.tsx";
import Panel from "common/NewLook/Panel.tsx";
import { NEW_LOOK_GAP } from "common/NewLook/tokens.ts";
import PublicBotSummarySkeleton from "features/bots/PublicBots/PublicBotSummarySkeleton.tsx";
import { phoneMaxWidth } from "common/NewLook/layout.ts";
import DirectionFieldsRow from "common/Grids/DirectionFieldsRow";

interface PublicBotSummaryProps {
  botUUID?: string;
}

const PublicBotSummary = ({ botUUID }: PublicBotSummaryProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const history = useHistory();
  const { analytics } = useContext(AnalyticsContext);
  const [modalOpen, setModalOpen] = useState(false);
  const [measureRef, { width }] = useMeasure<HTMLDivElement>();
  const appTheme = useTheme();
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const {
    botType,
    publicBotGeneralData,
    errors,
    botId,
    isFetching,
    symbols,
    isLoading,
  } = useContext(BotFormContext)!;
  const query = useQuery();
  const botRank = query.get(BOT_RANK_QUERY_PARAM) ?? undefined;
  const parsedBotRank = botRank !== undefined ? parseInt(botRank) : undefined;
  const isSignalsBot = botType === BotType.SIGNAL;

  const { nameInput, nameError } = useBotNameInput({
    initialize: true,
  });
  const {
    longShortSelect,
    marginTypeSelect,
    multiplierSelect,
    warning: leverageWarning,
    showLeverageSection,
  } = useMarginForm();
  useAdvanced({ openMessage });
  useAlternativeUsdPairs({});
  useKeywordToSymbol();

  const {
    symbolSelector,
    accountSelector,
    symbolError,
    accountError,
    accountIsValid,
    symbolIsValid,
  } = useSymbolAndAccountSelectors({
    defaultExchange: publicBotGeneralData?.exchange,
    defaultSymbols: publicBotGeneralData?.symbols,
    defaultBlacklistSymbols: publicBotGeneralData?.blacklist_symbols,
    initialize: !!publicBotGeneralData,
  });

  useEffect(() => {
    analytics?.dcaBotPublicBotCopyViewWeb({ botRank: parsedBotRank });
  }, [analytics]);

  const onEditClick = () => {
    if (botUUID) {
      analytics?.dcaBotEditBeforeCopyClicked({});
      history.push(getPublicBotEditPath(botType, botUUID));
    }
  };

  const onShowBotConfigurationClick = () => {
    setModalOpen(true);
    analytics?.dcaBotCopyBotConfigurationViewClicked({});
  };

  const allowOpenSymbolDrawer = !isSymbol(symbols) && !!symbols[0];
  const openSymbolDrawer = () => {
    return history.push(
      getCoinExtraDetailsRoute(location.pathname, symbols[0])
    );
  };

  const getGridsSelector = () => {
    if (botType !== BotType.GRID) return;
    return (
      <GridsSelector style={{ marginTop: 16 }} isCopyPublicBotSummary={true} />
    );
  };

  const hasError =
    !!nameError || !accountIsValid || !symbolIsValid || !!errors.length;
  const singleColumnLayout = width < 400;

  const fields = (
    <>
      <ItemWrapper>{nameInput}</ItemWrapper>
      <ItemWrapper>
        <AmountSelector isCopyPublicBotSummary={true} />
      </ItemWrapper>
      <ItemWrapper>
        <SubGroupSelect />
      </ItemWrapper>
      <ItemWrapper>
        <EqualColumnsGrid
          numColumns={singleColumnLayout || isSignalsBot ? 1 : 2}
        >
          <div>
            <FormFieldTitle>{t("labels.account")}</FormFieldTitle>
            {accountSelector}
            {accountError}
          </div>
          {!isSignalsBot && (
            <div>
              <FormFieldTitle
                onClick={allowOpenSymbolDrawer ? openSymbolDrawer : undefined}
              >
                {t("labels.symbol")}
                {allowOpenSymbolDrawer && (
                  <Tooltip title={t("tooltips.view_symbol_details")}>
                    <StyledOpenInNewIcon />
                  </Tooltip>
                )}
              </FormFieldTitle>
              {symbolSelector}
              {symbolError}
            </div>
          )}
        </EqualColumnsGrid>
      </ItemWrapper>
      {getGridsSelector()}
      {showLeverageSection && !isSignalsBot && (
        <ItemWrapper>
          {longShortSelect ? (
            <DirectionFieldsRow
              direction={longShortSelect}
              fields={
                <>
                  {marginTypeSelect}
                  {multiplierSelect}
                </>
              }
              columnGap={8}
              style={{ marginTop: 16 }}
            />
          ) : (
            <EqualColumnsGrid
              numColumns={2}
              style={{ marginTop: 16 }}
              columnGap={8}
            >
              {marginTypeSelect}
              {multiplierSelect}
            </EqualColumnsGrid>
          )}
          {leverageWarning}
        </ItemWrapper>
      )}
      {isSignalsBot && (
        <EqualColumnsGrid numColumns={1} style={{ display: "none" }}>
          <MultiSymbolMarginSelect />
        </EqualColumnsGrid>
      )}
      <BotFormSummary />
      <Link
        onClick={onShowBotConfigurationClick}
        style={{ marginTop: 16, display: "block" }}
      >
        {t("buttons.show_bot_configuration")}
      </Link>
      <Flex style={{ columnGap: 12, marginTop: 16 }}>
        <Button style={{ width: "100%" }} onClick={onEditClick}>
          {t("buttons.edit_bot")}
        </Button>
        <SaveBotButton
          copiedFrom={botId}
          disabled={hasError}
          analyticsBotOriginType={DcaBotCreateeditOrigin.COPY_AS_IS}
          errorMessage={t("messages.errors.resolve_issues")}
        />
      </Flex>
    </>
  );
  const configurationModal = (
    <BotConfigurationModal
      bot={publicBotGeneralData}
      open={modalOpen}
      closeModal={() => setModalOpen(false)}
    />
  );

  if (appTheme.isNewLook) {
    // New look: the copy form as a titled panel; skeleton fields while the
    // bot loads (the real ones stay mounted, hidden, so they initialize).
    const awaitingBot = isLoading || (isFetching && !publicBotGeneralData);
    return (
      <NewLookWrapper $isBottomNavigator={isBottomNavigator}>
        <Panel title={isBottomNavigator ? undefined : t("tabs.bot_summary")}>
          <div style={{ position: "relative" }} ref={measureRef}>
            {awaitingBot && <PublicBotSummarySkeleton />}
            <NewLookFields $hidden={awaitingBot}>
              <LoadingSpinnerOverlay
                isLoading={isFetching && !awaitingBot}
                backgroundColor={appTheme.colors.cardBgColor}
                spinnerSize="large"
                hideWithOpacity={true}
              >
                {fields}
              </LoadingSpinnerOverlay>
            </NewLookFields>
          </div>
        </Panel>
        {isSignalsBot && (
          <Panel>
            <SignalsSummary style={{ padding: 0 }} />
          </Panel>
        )}
        {configurationModal}
      </NewLookWrapper>
    );
  }

  return (
    <div>
      <SummaryWrapper>
        {!isBottomNavigator && (
          <Title level={2} style={{ marginBottom: 12 }}>
            {t("tabs.bot_summary")}
          </Title>
        )}
        <div style={{ position: "relative", height: "100%" }} ref={measureRef}>
          <LoadingSpinnerOverlay
            isLoading={isFetching || isLoading}
            hideContentWhileLoading={isLoading}
            backgroundColor={
              isBottomNavigator
                ? appTheme.colors.layoutBgColor
                : appTheme.colors.cardBgColor
            }
            spinnerSize="large"
            hideWithOpacity={true}
          >
            {fields}
          </LoadingSpinnerOverlay>
        </div>
      </SummaryWrapper>
      {isSignalsBot && (
        <SummaryWrapper style={{ marginTop: 16 }}>
          <SignalsSummary style={{ padding: 0 }} />
        </SummaryWrapper>
      )}
      {configurationModal}
    </div>
  );
};

export default PublicBotSummary;

const ItemWrapper = styled.div`
  margin-bottom: 16px;
`;
const NewLookWrapper = styled.div<{ $isBottomNavigator: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${NEW_LOOK_GAP}px;
  ${(p) =>
    p.$isBottomNavigator &&
    `
    height: 100%;
    padding: 16px;
    overflow-y: auto;
  `};
`;
const NewLookFields = styled.div<{ $hidden?: boolean }>`
  ${(p) => p.$hidden && "display: none;"};
`;
const StyledOpenInNewIcon = styled(OpenInNewIcon)`
  margin-left: 8px;
  width: 10px;
  fill: ${(p) => p.theme.colors.primaryGrey};
`;
const SummaryWrapper = styled.div`
  border: ${(p) => p.theme.borders.medium};
  border-radius: 8px;
  background-color: ${(p) => p.theme.colors.cardBgColor};
  height: min-content;
  ${paddingStyles};
  @media (max-width: ${phoneMaxWidth}) {
    background-color: ${(p) => p.theme.colors.layoutBgColor};
    border: none;
    overflow-y: auto;
    height: 100%;
  }
`;
